import { useState } from 'react';
import { Modal } from './common';

/**
 * A declarative create/edit dialog.
 *
 * `fields` is a list of {name, label, type, options, required, ...}. The whole
 * settings section reuses this rather than hand-rolling nine near-identical
 * forms, so validation and the busy/error handling behave the same everywhere.
 *
 * type: text | number | date | select | checkbox | textarea | multiselect
 *
 * `when(values)` hides a field until another answer calls for it — สร้างแพ็กเกจ
 * asks for a count or for a value depending on the ประเภท chosen, and asking for
 * both at once would leave whichever one does not apply to be ignored silently.
 */
export default function FormModal({
  title, fields, initial = {}, submitLabel = 'บันทึก', onSubmit, onClose, children,
}) {
  const [values, setValues] = useState(() => {
    const v = { ...initial };
    fields.forEach((f) => {
      if (v[f.name] === undefined) {
        v[f.name] = f.type === 'checkbox' ? false : f.type === 'multiselect' ? [] : '';
      }
    });
    return v;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (name, value) => setValues((v) => ({ ...v, [name]: value }));

  const shown = fields.filter((f) => !f.when || f.when(values));

  const submit = async () => {
    const missing = shown.find((f) => f.required && !String(values[f.name] ?? '').trim());
    if (missing) {
      setError(`กรุณากรอก${missing.label}`);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await onSubmit(values);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn-outline" onClick={onClose} disabled={busy}>ยกเลิก</button>
          <button className="btn-solid" onClick={submit} disabled={busy}>
            {busy ? 'กำลังบันทึก…' : submitLabel}
          </button>
        </>
      }
    >
      {error && <div className="login-error">{error}</div>}
      {children}

      <div className="grid-2">
        {shown.map((f) => (
          <div
            key={f.name}
            className="field"
            style={f.wide ? { gridColumn: '1 / -1' } : undefined}
          >
            <div className={f.showCount ? 'field-count' : undefined}>
              <label>{f.label}{f.required && <span style={{ color: 'var(--red-600)' }}> *</span>}</label>
              {f.showCount && (
                <span className="counter">
                  {String(values[f.name] || '').length}/{f.maxLength}
                </span>
              )}
            </div>

            {f.type === 'display' ? (
              <strong className="form-display-value">{f.render(values)}</strong>
            ) : f.type === 'select' ? (
              <select value={values[f.name]} onChange={(e) => set(f.name, e.target.value)}>
                <option value="">{f.placeholder || 'เลือก…'}</option>
                {f.options.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : f.type === 'checkbox' ? (
              <label className={`radio${values[f.name] ? ' on' : ''}`} style={{ paddingTop: 6 }}>
                <input
                  type="checkbox"
                  checked={!!values[f.name]}
                  onChange={(e) => set(f.name, e.target.checked)}
                />
                {f.hint || 'เปิดใช้งาน'}
              </label>
            ) : f.type === 'multiselect' ? (
              <div className="multiselect">
                {f.options.map((o) => {
                  const on = (values[f.name] || []).includes(o.value);
                  return (
                    <button
                      type="button"
                      key={o.value}
                      className={`staff-chip small${on ? '' : ' off'}`}
                      onClick={() => set(
                        f.name,
                        on
                          ? values[f.name].filter((x) => x !== o.value)
                          : [...(values[f.name] || []), o.value],
                      )}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            ) : f.type === 'textarea' ? (
              <textarea
                rows={3}
                value={values[f.name]}
                onChange={(e) => set(f.name, e.target.value)}
                placeholder={f.placeholder}
              />
            ) : (
              <input
                type={f.type || 'text'}
                value={values[f.name]}
                maxLength={f.maxLength}
                min={f.min}
                step={f.step}
                onChange={(e) => set(f.name, e.target.value)}
                placeholder={f.placeholder}
              />
            )}

            {f.hint && f.type !== 'checkbox' && (
              <span style={{ fontSize: 11.5, color: 'var(--ink-400)' }}>{f.hint}</span>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}
